import React, { useState } from 'react';
import cheksteLogoWhite from '../assets/chekste_logo_white_reverse_web.png';
import { 
  X, Download, Mail, Loader2, FileText, MapPin, DollarSign, 
  TrendingUp, Calendar, Ruler, Home, Bed, Bath, CheckCircle2,
  AlertTriangle, XCircle, Building2, Target, Shield, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { getCanonicalDecision } from '../utils/canonicalStateSerializer';

/**
 * Format currency as whole dollars (no cents)
 * Matches the main UI formatWholeDollars function
 */
const formatWholeDollars = (value) => {
  if (value === null || value === undefined) return '0';
  // PROBLEM 1: tolerate a formatted string ("1,500,000") instead of collapsing to '0'.
  const num = typeof value === 'string' ? parseFloat(value.replace(/[$,\s]/g, '')) : value;
  if (num === null || num === undefined || isNaN(num)) return '0';
  return Math.round(num).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

/**
 * PDFPreviewModal - Shows a preview of the PDF report before download/send
 * 
 * CRITICAL: This modal must render the correct format based on analysis.strategy
 * - 'flip' -> Fix & Flip format with PASS/NEGOTIATE/NO PASS recommendation
 * - 'rental' -> Rental format with DSCR/Cash flow metrics, rental-specific recommendation
 * 
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - analysis: object - the current active analysis state (MUST include .strategy)
 * - lang: 'en' | 'es'
 * - maoRule: number - user's selected MAO rule percentage (default 70)
 * - onDownloadPDF: async function
 * - onSendEmail: function - opens email modal
 */
const PDFPreviewModal = ({ 
  isOpen, 
  onClose, 
  analysis, 
  lang = 'en',
  maoRule = 70,
  onDownloadPDF,
  onSendEmail
}) => {
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !analysis) return null;

  // CRITICAL: Determine strategy from analysis state
  const strategy = analysis.strategy || 'flip';
  const isRental = strategy === 'rental';
  const isFlip = strategy === 'flip';
  const isValuation = strategy === 'valuation';

  const handleDownload = async () => {
    setDownloading(true);
    try {
      console.log('[PDFPreviewModal] Calling onDownloadPDF...');
      await onDownloadPDF();
      console.log('[PDFPreviewModal] onDownloadPDF completed');
    } catch (error) {
      console.error('[PDFPreviewModal] Download error:', error);
      toast.error(lang === 'en' ? `Failed to download PDF: ${error.message}` : `Error al descargar PDF: ${error.message}`);
    } finally {
      setDownloading(false);
    }
  };

  // ============================================================
  // P0 FIX: USE getCanonicalDecision() AS SINGLE SOURCE OF TRUTH
  // REMOVED: Local deal_rating mapping that diverged from Results page
  // ============================================================
  const getFlipRecommendation = () => {
    // P0 FIX: Use canonical decision helper - SINGLE SOURCE OF TRUTH
    const canonicalDecision = getCanonicalDecision(analysis, lang);
    
    if (canonicalDecision.color === 'green') {
      return {
        label: canonicalDecision.label,
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-300',
        textColor: 'text-green-700',
        icon: CheckCircle2,
        description: lang === 'en' 
          ? 'This deal shows strong profit potential with favorable fundamentals.'
          : 'Este trato muestra un fuerte potencial de ganancia con buenos fundamentos.'
      };
    } else if (canonicalDecision.color === 'amber') {
      return {
        label: canonicalDecision.label,
        color: 'amber',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-300',
        textColor: 'text-amber-700',
        icon: AlertTriangle,
        description: lang === 'en'
          ? 'This deal may work with better terms or a lower purchase price.'
          : 'Este trato podría funcionar con mejores términos o un precio más bajo.'
      };
    }
    // PASS
    return {
      label: canonicalDecision.label,
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      textColor: 'text-red-700',
      icon: XCircle,
      description: lang === 'en'
        ? 'This deal does not meet investment criteria based on current numbers.'
        : 'Este trato no cumple con los criterios de inversión según los números actuales.'
    };
  };

  // ============================================================
  // RENTAL RECOMMENDATION — the label comes from getCanonicalDecision, not from here.
  // This called getCanonicalDecision but then re-derived its OWN label strings off
  // .color, so it was a second copy of the mapping that happened to agree. The results
  // page and the PDF must never be able to word the same verdict differently.
  // ============================================================
  const getRentalRecommendation = () => {
    // P0 FIX: Use canonical decision helper - SINGLE SOURCE OF TRUTH
    const canonicalDecision = getCanonicalDecision(analysis, lang);
    const rentalAnalysis = analysis.rental_analysis || {};
    const monthlyCashflow = rentalAnalysis.monthly_cashflow || 0;
    
    if (canonicalDecision.color === 'green') {
      return {
        label: canonicalDecision.label,
        color: 'green',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-300',
        textColor: 'text-green-700',
        icon: CheckCircle2,
        description: lang === 'en' 
          ? 'This property shows strong rental income potential with positive cash flow.'
          : 'Esta propiedad muestra un fuerte potencial de ingresos por alquiler con flujo de caja positivo.'
      };
    } else if (canonicalDecision.color === 'amber') {
      return {
        label: canonicalDecision.label,
        color: 'amber',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-300',
        textColor: 'text-amber-700',
        icon: AlertTriangle,
        description: lang === 'en'
          ? 'This property has moderate rental potential. Review the numbers carefully.'
          : 'Esta propiedad tiene potencial de alquiler moderado. Revise los números cuidadosamente.'
      };
    }
    // PASS
    return {
      label: canonicalDecision.label,
      color: 'red',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      textColor: 'text-red-700',
      icon: XCircle,
      description: lang === 'en'
        ? 'This property does not meet rental investment criteria with current assumptions.'
        : 'Esta propiedad no cumple con los criterios de inversión de alquiler con los supuestos actuales.'
    };
  };

  // Get appropriate recommendation based on strategy
  const recommendation = isRental ? getRentalRecommendation() : getFlipRecommendation();
  const RecommendationIcon = recommendation.icon;

  // Get ARV mode display
  const getArvModeDisplay = () => {
    const mode = analysis.active_arv_mode || 'both';
    const modeLabel = analysis.arv_mode_label;
    
    if (modeLabel) return modeLabel;
    
    const modeMap = {
      'system': lang === 'en' ? 'System Only' : 'Solo Sistema',
      'user': lang === 'en' ? 'User Only' : 'Solo Usuario',
      'both': lang === 'en' ? 'Blended' : 'Combinado'
    };
    return modeMap[mode] || (lang === 'en' ? 'Blended' : 'Combinado');
  };

  // Get confidence display
  const getConfidenceDisplay = () => {
    const confidence = analysis.arv_confidence || 'low';
    // Rounded, same as pdf_service.py: the engine stores 79.666…; both surfaces say 80/100.
    const score = Math.round(Number(analysis.arv_confidence_score) || 0);
    
    const labels = {
      'high': lang === 'en' ? 'HIGH' : 'ALTO',
      'moderate': lang === 'en' ? 'MODERATE' : 'MEDIO',
      'low': lang === 'en' ? 'LOW' : 'BAJO'
    };
    
    return `${labels[confidence] || confidence.toUpperCase()} (${score}/100)`;
  };

  // Get comps for display
  // Same logic as pdf_service.py - user comps first, then system comps used for ARV
  const allSalesComps = analysis.sales_comps || [];
  const userComps = allSalesComps.filter(c => c.source === 'user');
  const arvDetails = analysis.arv_details || {};
  // A REFINANCE has no purchase price, and a user-supplied value must never be presented
  // as ours in the artefact a lender reads.
  const isRefi = (analysis.rental_analysis?.rental_purchase_type) === 'refinancing';
  const isUserArv = analysis.arv_is_user_override === true;
  // GAP #2: honest-blocked ARV — no confident ARV-derived number (value, equity) in the
  // lender-facing preview; it must match the app's "add renovated comps" state.
  // G3: same rule as the page — the active mode's record and the engine's verdict flag
  // decide; arv_status only for documents that predate the flag.
  const _rec = analysis?.arv_by_mode?.[analysis?.active_arv_mode] || null;
  const _flag = typeof _rec?.verdict_reliable === 'boolean' ? _rec.verdict_reliable
    : (typeof analysis?.verdict_reliable === 'boolean' ? analysis.verdict_reliable : undefined);
  const arvBlocked = _flag !== undefined
    ? !_flag
    : ['NO_RENOVATED_COMPS', 'UNABLE_TO_COMPUTE', 'UNIT_COUNT_UNCONFIRMED'].includes((_rec?.arv_details || arvDetails).arv_status);
  // Why this ARV is weak, per fallback tier. Keyed off arv_warning_key so the Spanish
  // preview does not print the engine's English prose; that prose is the fallback.
  const ARV_TIER_NOTES = {
    floor_anchored_no_livable_comps: {
      en: 'No livable comparable sales or listings nearby — this is a floor estimate, not a comp-based value.',
      es: 'No hay ventas ni listados habitables cerca — esto es una estimación de piso, no un valor basado en comps.',
    },
    floor_anchored_rescue_below_floor: {
      en: 'No livable comparable sales or listings nearby — this is a floor estimate, not a comp-based value.',
      es: 'No hay ventas ni listados habitables cerca — esto es una estimación de piso, no un valor basado en comps.',
    },
    as_is_sold_above_livable_floor: {
      en: 'Limited renovated comps here — this uses the livable sold comps available.',
      es: 'Comps renovados limitados aquí — esto usa las ventas habitables disponibles.',
    },
    estimate_from_for_sale_listings: {
      en: 'Based on for-sale listings (asking prices, not closed sales).',
      es: 'Basado en propiedades en venta (precios pedidos, no ventas cerradas).',
    },
    ppsf_fallback_no_bed_data: {
      en: 'Comparables lacked unit data — valued per square foot instead.',
      es: 'Los comparables no traen datos de unidades — valuado por pie cuadrado.',
    },
    unit_count_unconfirmed: { en: 'Confirm the property type (duplex, triplex or fourplex) to compute the value.', es: 'Confirma el tipo de propiedad (dúplex, tríplex o cuádruplex) para calcular el valor.' },
  };
  const arvTierNote = (ARV_TIER_NOTES[arvDetails.arv_warning_key]
    || {})[lang === 'es' ? 'es' : 'en'] || arvDetails.arv_warning || null;
  // NO RAW-POOL FALLBACK. `final_comps` is the set the ARV actually used, already past the
  // listing/distressed ban. Falling back to every system comp in the pool made this modal
  // another surface that could present an asking price as a comparable sale — the same
  // hole found in ComparableManager, the Results tables and pdf_service. This is the
  // lender-facing preview, so it is the last place that should improvise.
  const systemCompsUsed = arvDetails.final_comps || [];
  const salesComps = [...userComps, ...systemCompsUsed];
  const flipAnalysis = analysis.flip_analysis || {};
  const rentalAnalysis = analysis.rental_analysis || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="pdf-preview-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-800 to-slate-700">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {lang === 'en' ? 'PDF Preview' : 'Vista Previa del PDF'}
              {/* Show strategy badge */}
              <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                isValuation ? 'bg-purple-500/30 text-purple-200' :
                isRental ? 'bg-green-500/30 text-green-200' : 'bg-blue-500/30 text-blue-200'
              }`}>
                {isValuation ? (lang === 'en' ? 'Valuation' : 'Valoración') :
                 isRental ? (lang === 'en' ? 'Rental' : 'Alquiler') : 'Fix & Flip'}
              </span>
            </h2>
          </div>
          <button 
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors p-1"
            data-testid="close-preview-btn"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Preview Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-100">
          <div className="bg-white rounded-xl shadow-lg max-w-3xl mx-auto">
            {/* Report Header */}
            <div className={`text-white p-6 rounded-t-xl ${
              isValuation
                ? 'bg-gradient-to-r from-[#6b21a8] to-[#a855f7]'
                : isRental 
                  ? 'bg-gradient-to-r from-[#166534] to-[#22c55e]' 
                  : 'bg-gradient-to-r from-[#1e3a5f] to-[#2563eb]'
            }`}>
              <div className="flex items-center justify-between mb-4">
                {/* Reverse artwork: this header is always a dark gradient — navy for
                    flip, green for rental, purple for valuation — so the dark lockup
                    would disappear into it. h-8 matches the 2xl text it replaces. */}
                <img src={cheksteLogoWhite} alt="CHEKSTE" className="h-8 w-auto" />
                <span className={`text-sm ${isValuation ? 'text-purple-200' : isRental ? 'text-green-200' : 'text-blue-200'}`}>
                  {isValuation
                    ? (lang === 'en' ? 'Property Valuation Report' : 'Reporte de Valoración de Propiedad')
                    : isRental 
                      ? (lang === 'en' ? 'Rental Investment Analysis' : 'Análisis de Inversión de Alquiler')
                      : (lang === 'en' ? 'Fix & Flip Analysis Report' : 'Reporte de Análisis Fix & Flip')
                  }
                </span>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold">{analysis.address}</h2>
                  <p className={isValuation ? 'text-purple-100' : isRental ? 'text-green-100' : 'text-blue-100'}>
                    {analysis.city}, {analysis.state} {analysis.zip_code}
                  </p>
                </div>
              </div>
            </div>

            {/* Property Details */}
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                {lang === 'en' ? 'Property Summary' : 'Resumen de la Propiedad'}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                {/* Property Type - CRITICAL for comps consistency */}
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-xs text-slate-500">{lang === 'en' ? 'Type' : 'Tipo'}</div>
                    <div className="font-semibold">{analysis.property_type || analysis.property_snapshot?.property_type || 'Single Family'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-xs text-slate-500">
                      {isRefi
                        ? (lang === 'en' ? 'Appraised Value' : 'Valor Tasado')
                        // nosemgrep: purchase-price-must-be-refinance-aware -- isRefi ternary above
                        : (lang === 'en' ? 'Purchase Price' : 'Precio')}
                      {isUserArv && (
                        <span className="text-amber-700 font-semibold">
                          {lang === 'en' ? ' (your figure)' : ' (Valor ingresado por el usuario)'}
                        </span>
                      )}
                    </div>
                    <div className="font-semibold">${formatWholeDollars(analysis.purchase_price)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Bed className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-xs text-slate-500">{lang === 'en' ? 'Beds' : 'Hab.'}</div>
                    <div className="font-semibold">{analysis.beds || 'N/A'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Bath className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-xs text-slate-500">{lang === 'en' ? 'Baths' : 'Baños'}</div>
                    <div className="font-semibold">{analysis.baths || 'N/A'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Ruler className="w-4 h-4 text-slate-500" />
                  <div>
                    <div className="text-xs text-slate-500">{lang === 'en' ? 'Sq Ft' : 'Pies²'}</div>
                    <div className="font-semibold">{analysis.sqft?.toLocaleString() || 'N/A'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Investment Recommendation - Strategy-Specific (Hidden for Valuation) */}
            {!isValuation && (
            <div className={`p-6 border-b border-slate-200 ${recommendation.bgColor}`}>
              <h3 className="text-lg font-semibold text-slate-800 mb-4">
                {isRental 
                  ? (lang === 'en' ? 'Rental Investment Rating' : 'Calificación de Inversión de Alquiler')
                  : (lang === 'en' ? 'Investment Recommendation' : 'Recomendación de Inversión')
                }
              </h3>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-full ${
                  recommendation.color === 'green' ? 'bg-green-500' :
                  recommendation.color === 'amber' ? 'bg-amber-500' :
                  'bg-red-500'
                }`}>
                  <RecommendationIcon className="w-8 h-8 text-white" />
                </div>
                <div>
                  <div className={`text-2xl font-bold ${recommendation.textColor}`}>
                    {recommendation.label}
                  </div>
                  <p className="text-slate-600 text-sm">{recommendation.description}</p>
                </div>
              </div>
            </div>
            )}

            {/* ============================================================ */}
            {/* VALUATION-SPECIFIC METRICS (Only shown for Valuation strategy) */}
            {/* ============================================================ */}
            {isValuation && (
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">
                  {lang === 'en' ? 'Property Valuation Summary' : 'Resumen de Valoración de Propiedad'}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">{lang === 'en' ? 'Estimated Value' : 'Valor Estimado'}</div>
                    {/* The value is always printed when one exists; only ARV-DERIVED figures
                        (equity below) stay blocked. See Results.js for the full reasoning. */}
                    {arvBlocked ? (
                      <>
                        {analysis.arv > 0 && (
                          <div className="text-2xl font-bold text-amber-800">${formatWholeDollars(analysis.arv)}</div>
                        )}
                        <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
                          {lang === 'en' ? 'Low confidence — limited comps' : 'Baja confianza — comps limitados'}
                        </span>
                        <div className="text-xs font-semibold text-amber-700 mt-1">
                          {lang === 'en' ? 'Add your comps for a stronger ARV' : 'Agrega tus comps para un ARV más preciso'}
                        </div>
                        {arvTierNote && (
                          <div className="text-[11px] text-slate-500 mt-1 leading-snug">{arvTierNote}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-2xl font-bold text-purple-600">${formatWholeDollars(analysis.arv)}</div>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">{lang === 'en' ? 'Potential Equity' : 'Capital Potencial'}</div>
                    {arvBlocked ? (
                      <div className="text-base font-bold text-amber-700">{lang === 'en' ? 'Needs reliable ARV' : 'Necesita ARV confiable'}</div>
                    ) : (
                      <div className={`text-2xl font-bold ${(analysis.arv - analysis.purchase_price) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${formatWholeDollars(analysis.arv - analysis.purchase_price)}
                      </div>
                    )}
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="text-xs text-slate-500 mb-1">{lang === 'en' ? 'ARV Confidence' : 'Confianza ARV'}</div>
                    <div className={`text-lg font-bold ${
                      analysis.arv_confidence === 'high' ? 'text-green-600' :
                      analysis.arv_confidence === 'moderate' ? 'text-amber-600' :
                      'text-red-600'
                    }`}>
                      {analysis.arv_confidence === 'high' ? (lang === 'en' ? 'HIGH' : 'ALTO') :
                       analysis.arv_confidence === 'moderate' ? (lang === 'en' ? 'MODERATE' : 'MEDIO') :
                       (lang === 'en' ? 'LOW' : 'BAJO')} ({Math.round(Number(analysis.arv_confidence_score) || 0)}/100)
                    </div>
                  </div>
                </div>
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <p className="text-sm text-purple-700">
                    {lang === 'en' 
                      ? 'This valuation is based on comparable sales analysis. The estimated value represents the current market value of the property.'
                      : 'Esta valoración se basa en el análisis de ventas comparables. El valor estimado representa el valor de mercado actual de la propiedad.'}
                  </p>
                </div>
              </div>
            )}

            {/* ============================================================ */}
            {/* RENTAL-SPECIFIC METRICS (Only shown for Rental strategy) */}
            {/* ============================================================ */}
            {isRental && rentalAnalysis && (
              <>
                {/* Primary Rental Metrics */}
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {lang === 'en' ? 'Rental Cash Flow Analysis' : 'Análisis de Flujo de Caja de Alquiler'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Monthly Rent' : 'Renta Mensual'}</div>
                      <div className="text-lg font-semibold text-green-700">${formatWholeDollars(rentalAnalysis.monthly_rent)}</div>
                    </div>
                    <div className={`p-3 rounded-lg ${rentalAnalysis.monthly_cashflow >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Monthly Cash Flow' : 'Flujo Mensual'}</div>
                      <div className={`text-lg font-semibold ${rentalAnalysis.monthly_cashflow >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${formatWholeDollars(rentalAnalysis.monthly_cashflow)}
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${(rentalAnalysis.dscr || 0) >= 1.25 ? 'bg-green-50' : (rentalAnalysis.dscr || 0) >= 1.0 ? 'bg-amber-50' : 'bg-red-50'}`}>
                      <div className="text-xs text-slate-500">DSCR</div>
                      <div className={`text-lg font-semibold ${
                        (rentalAnalysis.dscr || 0) >= 1.25 ? 'text-green-700' : 
                        (rentalAnalysis.dscr || 0) >= 1.0 ? 'text-amber-700' : 'text-red-700'
                      }`}>
                        {(rentalAnalysis.dscr || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Annual Cash Flow' : 'Flujo Anual'}</div>
                      <div className={`text-lg font-semibold ${(rentalAnalysis.annual_cashflow || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${formatWholeDollars(rentalAnalysis.annual_cashflow)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Rental Return Metrics */}
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {lang === 'en' ? 'Rental Return Metrics' : 'Métricas de Retorno de Alquiler'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Cap Rate' : 'Tasa Cap'}</div>
                      <div className="text-lg font-semibold">{(rentalAnalysis.cap_rate || 0).toFixed(1)}%</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Cash-on-Cash Return' : 'Retorno Cash-on-Cash'}</div>
                      <div className="text-lg font-semibold">{(rentalAnalysis.cash_on_cash_return || 0).toFixed(1)}%</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Total Investment' : 'Inversión Total'}</div>
                      {/* The API field is cash_invested. `total_investment` is not part of
                          the rental payload, so this printed "$0" — in the document a
                          lender reads. */}
                      <div className="text-lg font-semibold">${formatWholeDollars(rentalAnalysis.cash_invested)}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Financing Type' : 'Tipo de Financiamiento'}</div>
                      <div className="text-lg font-semibold">
                        {rentalAnalysis.financing_type === 'dscr' ? 'DSCR Loan' :
                         rentalAnalysis.financing_type === 'conventional' ? (lang === 'en' ? 'Conventional' : 'Convencional') :
                         lang === 'en' ? 'Cash' : 'Efectivo'}
                      </div>
                      {/* Say WHY it is cash. A lender reading "Cash" on a deal the
                          borrower asked to finance should see that the condition, not
                          the borrower, decided that. */}
                      {rentalAnalysis.dscr_eligible === false
                        && rentalAnalysis.financing_type !== 'dscr' && (
                        <div className="text-xs text-amber-700 mt-1">
                          {lang === 'en'
                            ? 'Condition is not DSCR-eligible — analyzed as cash.'
                            : 'La condición no califica para DSCR — analizado como efectivo.'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rental Monthly Costs Breakdown */}
                {rentalAnalysis.monthly_costs_breakdown && (
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-4">
                      {lang === 'en' ? 'Monthly Operating Costs' : 'Costos Operativos Mensuales'}
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {/* No mortgage row here. There is no `mortgage` key in this
                          breakdown (it never rendered), and under the PITIA model the loan
                          payment is not an operating cost — the section's own `total`
                          excludes it. Adding it back would make these rows stop summing to
                          the total, and would double-count the payment shown above. */}
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500">{lang === 'en' ? 'Property Taxes' : 'Impuestos'}</div>
                        <div className="font-semibold">${formatWholeDollars(rentalAnalysis.monthly_costs_breakdown.property_taxes)}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500">{lang === 'en' ? 'Insurance' : 'Seguro'}</div>
                        <div className="font-semibold">${formatWholeDollars(rentalAnalysis.monthly_costs_breakdown.insurance)}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500">{lang === 'en' ? 'Vacancy Reserve' : 'Reserva Vacancia'}</div>
                        <div className="font-semibold">${formatWholeDollars(rentalAnalysis.monthly_costs_breakdown.vacancy_reserve)}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500">{lang === 'en' ? 'Maintenance' : 'Mantenimiento'}</div>
                        <div className="font-semibold">${formatWholeDollars(rentalAnalysis.monthly_costs_breakdown.maintenance_reserve)}</div>
                      </div>
                      <div className="p-2 bg-slate-50 rounded-lg">
                        <div className="text-xs text-slate-500">{lang === 'en' ? 'Property Mgmt' : 'Administración'}</div>
                        <div className="font-semibold">${formatWholeDollars(rentalAnalysis.monthly_costs_breakdown.property_management)}</div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="font-medium text-slate-700">{lang === 'en' ? 'Total Monthly Expenses' : 'Gastos Mensuales Totales'}</span>
                      {/* The breakdown carries its OWN total — use it.
                          `total_monthly_expenses` is not in the payload (it printed "$0").
                          Replacing it with a hand-rolled sum was no better: that sum named
                          `vacancy`, `maintenance` and `mortgage`, none of which exist here
                          (the keys are vacancy_reserve / maintenance_reserve), so it
                          silently dropped $260 and reported $607 instead of $867. Reading
                          the payload's own total removes the chance to mis-name a key. */}
                      <span className="text-lg font-bold text-slate-800">${formatWholeDollars(
                        rentalAnalysis.monthly_costs_breakdown.total
                      )}</span>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ============================================================ */}
            {/* FLIP-SPECIFIC METRICS (Only shown for Fix & Flip strategy) */}
            {/* ============================================================ */}
            {isFlip && (
              <>
                {/* ARV Section */}
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {lang === 'en' ? 'After Repair Value (ARV)' : 'Valor Después de Reparación (ARV)'}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <div className="text-sm text-slate-500 mb-1">
                        {lang === 'en' ? 'Estimated ARV' : 'ARV Estimado'}
                      </div>
                      {arvBlocked ? (
                        <>
                          {analysis.arv > 0 && (
                            <div className="text-2xl font-bold text-amber-800">
                              ${formatWholeDollars(analysis.arv)}
                            </div>
                          )}
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800">
                            {lang === 'en' ? 'Low confidence — limited comps' : 'Baja confianza — comps limitados'}
                          </span>
                          <div className="text-xs font-semibold text-amber-700 mt-1">
                            {lang === 'en' ? 'Add your comps for a stronger ARV' : 'Agrega tus comps para un ARV más preciso'}
                          </div>
                          {arvTierNote && (
                            <div className="text-[11px] text-slate-500 mt-1 leading-snug">{arvTierNote}</div>
                          )}
                        </>
                      ) : (
                        <div className="text-2xl font-bold text-blue-600">
                          ${formatWholeDollars(analysis.arv)}
                        </div>
                      )}
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500 mb-1">
                        {lang === 'en' ? 'ARV Mode' : 'Modo ARV'}
                      </div>
                      <div className="text-lg font-semibold text-slate-700">
                        {getArvModeDisplay()}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500 mb-1">
                        {lang === 'en' ? 'Confidence Level' : 'Nivel de Confianza'}
                      </div>
                      <div className="text-lg font-semibold text-slate-700">
                        {getConfidenceDisplay()}
                      </div>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <div className="text-sm text-slate-500 mb-1">
                        {lang === 'en' ? 'Comparables Used' : 'Comparables Usados'}
                      </div>
                      <div className="text-lg font-semibold text-slate-700">
                        {salesComps.length || analysis.comparable_count || 0}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Comparable Sales */}
                {salesComps.length > 0 && (
                  <div className="p-6 border-b border-slate-200">
                    <h3 className="text-lg font-semibold text-slate-800 mb-2">
                      {lang === 'en' ? 'Comparable Sales Used for ARV' : 'Ventas Comparables para ARV'}
                    </h3>
                    <p className="text-xs text-slate-500 mb-4 italic">
                      {analysis.active_arv_mode === 'user'
                        ? (lang === 'en' ? 'User-provided comparables selected for this analysis.' : 'Comparables proporcionados por el usuario.')
                        : analysis.active_arv_mode === 'system'
                        ? (lang === 'en' ? 'System comparables based on proximity and similarity.' : 'Comparables del sistema basados en proximidad y similitud.')
                        : (lang === 'en' ? 'Combination of system and user-provided comparables.' : 'Combinación de comparables del sistema y del usuario.')
                      }
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-slate-100">
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">#</th>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">
                              {lang === 'en' ? 'Address' : 'Dirección'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                              {lang === 'en' ? 'Sold Price' : 'Precio'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                              {lang === 'en' ? 'Sq Ft' : 'Pies²'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">$/SqFt</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                              {lang === 'en' ? 'Distance' : 'Dist.'}
                            </th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">
                              {lang === 'en' ? 'Sale Date' : 'Fecha'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesComps.slice(0, 10).map((comp, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                              <td className="px-3 py-2 text-slate-600">{idx + 1}</td>
                              <td className="px-3 py-2 text-slate-800 max-w-[200px] truncate">
                                {comp.display_address || comp.address || `Comp #${idx + 1}`}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-800">
                                ${formatWholeDollars(comp.sale_price || comp.price || 0)}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                {comp.sqft?.toLocaleString() || 'N/A'}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                ${comp.price_per_sqft || comp.ppsf || (comp.sqft ? Math.round((comp.sale_price || comp.price || 0) / comp.sqft) : 0)}
                              </td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                {comp.distance?.toFixed(2) || '0.00'} mi
                              </td>
                              <td className="px-3 py-2 text-right text-slate-600">
                                {comp.sale_date || comp.sold_date ? new Date(comp.sale_date || comp.sold_date).toLocaleDateString() : 'N/A'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Key Investment Metrics - Flip */}
                <div className="p-6 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4">
                    {lang === 'en' ? 'Key Investment Metrics' : 'Métricas Clave de Inversión'}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Rehab Estimate' : 'Costo Rehab'}</div>
                      <div className="text-lg font-semibold">${formatWholeDollars(analysis.rehab_estimate)}</div>
                    </div>
                    {/* Loan Costs - Critical for Cash vs Financed comparison */}
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Loan Costs' : 'Costos de Préstamo'}</div>
                      <div className="text-lg font-semibold">
                        ${formatWholeDollars(flipAnalysis.loan_costs || 0)}
                        {(flipAnalysis.loan_costs || 0) === 0 && (
                          <span className="text-xs ml-1 text-emerald-600">(Cash)</span>
                        )}
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${(flipAnalysis.net_profit || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Net Profit' : 'Ganancia Neta'}</div>
                      <div className={`text-lg font-semibold ${(flipAnalysis.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ${formatWholeDollars(flipAnalysis.net_profit)}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Total Project Cost' : 'Costo Total'}</div>
                      <div className="text-lg font-semibold">${formatWholeDollars(flipAnalysis.total_project_cost)}</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Profit Margin' : 'Margen'}</div>
                      <div className="text-lg font-semibold">{(flipAnalysis.profit_margin || 0).toFixed(1)}%</div>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <div className="text-xs text-slate-500">{lang === 'en' ? 'Target Offer' : 'Precio Objetivo'} ({maoRule}%)</div>
                      <div className="text-lg font-semibold">${formatWholeDollars(flipAnalysis.mao)}</div>
                    </div>
                  </div>
                </div>

                {/* Deal Score removed from investor-facing report — investors act on
                    profit, margin, and the BUY/NEGOTIATE/AVOID verdict, not a 0-100 score. */}
              </>
            )}

            {/* Report Footer */}
            <div className="p-4 bg-slate-50 rounded-b-xl text-center">
              <p className="text-xs text-slate-500">
                {lang === 'en' 
                  ? 'This report was generated by CHEKSTE. The analysis is based on automated calculations and market data. Please conduct your own due diligence before making investment decisions.'
                  : 'Este reporte fue generado por CHEKSTE. El análisis se basa en cálculos automatizados y datos de mercado. Realice su propia diligencia debida antes de tomar decisiones de inversión.'
                }
              </p>
              <p className="text-xs text-slate-400 mt-2">
                {new Date().toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className={`flex-1 px-6 py-3 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-all ${
              isRental ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'
            }`}
            data-testid="preview-download-btn"
          >
            {downloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            {lang === 'en' ? 'Download PDF' : 'Descargar PDF'}
          </button>
          <button
            onClick={() => {
              onClose();
              onSendEmail();
            }}
            className="flex-1 px-6 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 flex items-center justify-center gap-2 transition-all"
            data-testid="preview-email-btn"
          >
            <Mail className="w-5 h-5" />
            {lang === 'en' ? 'Send by Email' : 'Enviar por Correo'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 sm:flex-none px-6 py-3 bg-slate-100 text-slate-700 rounded-lg font-medium hover:bg-slate-200 flex items-center justify-center gap-2 transition-all"
            data-testid="preview-close-btn"
          >
            <X className="w-5 h-5" />
            {lang === 'en' ? 'Close Preview' : 'Cerrar Vista Previa'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFPreviewModal;
